import { posthog } from "posthog-js";
import { useLocation } from "preact-iso";
import { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { botUseCases } from "../../useCases.ts";
import { extractExcerpt, posts, ThemedBlogImage } from "./blog.tsx";
import {
  ArrowRightIcon,
  CodeBlock,
  CtaButton,
  LegalFooter,
} from "./components.tsx";
import { useAuth } from "./db.ts";
import { LoginCta } from "./login.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { talkToSkillRoute } from "./talkToSkill.tsx";
import { templatesRoute } from "./agency.tsx";
import { freeTrialTerms, PricingSection } from "./pricing.tsx";
import { landingSocialMeta, pricingSocialMeta } from "./socialMeta.ts";
import { ComparisonSection } from "./featureComparison.tsx";
import { fireAnalyticsEventLegacy } from "./utils.ts";

const frontUseCases = botUseCases.filter(({ category }) =>
  category === "front"
);

const teamUseCases = botUseCases.filter(({ category }) => category === "team");

const capIconClass =
  "w-6 h-6 text-text-secondary-light dark:text-text-secondary";

const MonitorIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={capIconClass}
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8m-4-4v4" />
  </svg>
);

const CompassIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={capIconClass}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" />
  </svg>
);

const PuzzleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={capIconClass}
  >
    <path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.968-.925a2.501 2.501 0 1 0-3.214 3.214c.446.166.855.497.925.968a.979.979 0 0 1-.276.837l-1.61 1.61a2.404 2.404 0 0 1-1.705.707 2.402 2.402 0 0 1-1.704-.706l-1.568-1.568a1.026 1.026 0 0 0-.877-.29c-.493.074-.84.504-1.02.968a2.5 2.5 0 1 1-3.237-3.237c.464-.18.894-.527.967-1.02a1.026 1.026 0 0 0-.289-.877l-1.568-1.568A2.402 2.402 0 0 1 1.998 12c0-.617.236-1.234.706-1.704L4.315 8.685a.98.98 0 0 1 .837-.276c.47.07.802.48.968.925a2.501 2.501 0 1 0 3.214-3.214c-.446-.166-.855-.497-.925-.968a.979.979 0 0 1 .276-.837l1.61-1.61a2.404 2.404 0 0 1 1.705-.707c.617 0 1.234.236 1.704.706l1.568 1.568c.23.23.556.338.877.29.493-.074.84-.504 1.02-.968a2.5 2.5 0 1 1 3.237 3.237c-.464.18-.894.527-.967 1.02z" />
  </svg>
);

const PlugIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={capIconClass}
  >
    <path d="M12 22v-5m0 0a7 7 0 0 0 7-7V2h-3v5h-8V2H5v8a7 7 0 0 0 7 7z" />
  </svg>
);

const BrainIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={capIconClass}
  >
    <path d="M12 2a6 6 0 0 0-6 6c0 1.66.68 3.16 1.76 4.24L12 16.49l4.24-4.25A5.98 5.98 0 0 0 18 8a6 6 0 0 0-6-6z" />
    <path d="M12 16.49V22" />
    <path d="M9 8a3 3 0 0 1 6 0" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={capIconClass}
  >
    <path d="M21 2v6h-6" />
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M3 22v-6h6" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
  </svg>
);

const capabilities: {
  icon: () => ComponentChildren;
  title: string;
  description: string;
}[] = [
  {
    icon: MonitorIcon,
    title: "Full VM",
    description:
      "Agents can use an on-demand virtual machine to run scripts, process data, and generate files.",
  },
  {
    icon: CompassIcon,
    title: "Browser",
    description:
      "Agents can browse the web, scrape pages, fill out forms, and research websites on your behalf.",
  },
  {
    icon: PuzzleIcon,
    title: "Pre-Built Skills",
    description:
      "Plug in pre-built skills — Google Calendar, Sheets, Docs, Gmail, Shopify, voice transcription, and web search.",
  },
  {
    icon: PlugIcon,
    title: "Custom Tools & APIs",
    description:
      "Connect your backend APIs as tools. The agent calls your endpoints to take real actions across your systems.",
  },
  {
    icon: BrainIcon,
    title: "Memory",
    description:
      "Remembers past context and customer history across conversations for seamless continuity.",
  },
  {
    icon: RefreshIcon,
    title: "Proactive Schedules",
    description:
      "Send reminders and run scheduled workflows. Recurring schedules require a paid plan.",
  },
];

const quickStartUseCases = [
  {
    title: "Life & Work Assistant",
    description:
      "Organize tasks, research a question, or set a reminder. Connect your calendar when you want help with scheduling.",
    url:
      "https://prompt2bot.com/talk-to-skill?url=tank%3A%40uriva%2Fp2b-personal-assistant",
  },
  {
    title: "Coder & Automation Agent",
    description:
      "Inspect repositories, run scripts, and connect APIs to automate repetitive operations.",
    url: "https://prompt2bot.com/talk-to-skill?url=tank%3A%40uriva%2Fp2b-coder",
  },
];

const iconClass = "w-6 h-6 text-text-secondary-light dark:text-text-secondary";

const GlobeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={iconClass}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={iconClass}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={iconClass}>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={iconClass}
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={iconClass}
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const PlusIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={iconClass}
  >
    <path strokeLinecap="round" d="M12 5v14m-7-7h14" />
  </svg>
);

const channels: {
  icon: () => ComponentChildren;
  name: string;
  detail: string;
}[] = [
  { icon: GlobeIcon, name: "Web", detail: "E2E encrypted via alice&bot" },
  { icon: WhatsAppIcon, name: "WhatsApp", detail: "Personal & Business" },
  { icon: TelegramIcon, name: "Telegram", detail: "Bots & groups" },
  { icon: MailIcon, name: "Email", detail: "Via AgentMail" },
  { icon: PhoneIcon, name: "Voice", detail: "WebRTC calls" },
  { icon: PlusIcon, name: "More", detail: "Slack, SMS, and beyond" },
];

const openSourceProjects = [
  {
    name: "alice-and-bot",
    url: "https://aliceandbot.com",
    description: "Chat infrastructure",
  },
  {
    name: "abstract-bot-api",
    url: "https://github.com/uriva/abstract-bot-api",
    description: "Multi-channel messaging",
  },
  {
    name: "ai-utils",
    url: "https://github.com/uriva/ai-utils",
    description: "Our harness",
  },
  {
    name: "safescript",
    url: "https://safescript.cc",
    description: "Safe code execution",
  },
  {
    name: "p2b-skills",
    url: "https://github.com/uriva/p2b-skills",
    description: "Reusable skills",
  },
  {
    name: "Tank",
    url: "https://github.com/tankpkg/tank",
    description: "Skill packaging",
  },
];

const ChevronLeftIcon = (
  { className = "w-4 h-4" }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = (
  { className = "w-4 h-4" }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const ZapIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const sectionClass =
  "min-h-screen flex flex-col items-center justify-center px-6 py-16 md:py-20";

const sectionHeadingClass =
  "text-3xl md:text-4xl font-display font-bold mb-3 text-text-primary-light dark:text-text-primary text-center";

const sectionSubheadingClass =
  "text-base md:text-lg text-text-tertiary-light dark:text-text-tertiary text-center mb-10 max-w-xl mx-auto";

type HeroTemplate = {
  id: string;
  name: string;
  division: string;
  description: string;
  url: string;
};

const curatedHeroTemplates: HeroTemplate[] = [
  {
    id: "lead-qualifier",
    name: "Lead Qualifier & Scheduler",
    division: "Sales",
    description:
      "Start with a sales strategy template, then add your qualification questions and connect a calendar for bookings.",
    url:
      "https://github.com/msitarzewski/agency-agents/blob/main/sales/sales-account-strategist.md",
  },
  {
    id: "ad-creative",
    name: "Ad Creative & Copy Strategist",
    division: "Marketing",
    description:
      "Draft ad copy, search ad variations, and creative testing plans for Google, Meta, and LinkedIn.",
    url:
      "https://github.com/msitarzewski/agency-agents/blob/main/paid-media/paid-media-creative-strategist.md",
  },
  {
    id: "personal-assistant",
    name: "Life & Work Assistant",
    division: "Assistant",
    description:
      "Organize tasks, reminders, and follow-ups in chat. Connect your calendar and configure schedules as needed.",
    url: "tank:@uriva/p2b-personal-assistant",
  },
  {
    id: "bookkeeper",
    name: "Bookkeeper & Controller",
    division: "Finance",
    description:
      "Use a finance template to organize expenses and prepare summaries from the records you provide. Review outputs before use.",
    url:
      "https://github.com/msitarzewski/agency-agents/blob/main/finance/finance-bookkeeper-controller.md",
  },
  {
    id: "web-researcher",
    name: "Autonomous Market Researcher",
    division: "Research",
    description:
      "Research competitors and industry updates, then bring the sources together in a structured brief.",
    url:
      "https://github.com/msitarzewski/agency-agents/blob/main/research/research-synthesist.md",
  },
  {
    id: "coder-agent",
    name: "Coder & DevOps Integrator",
    division: "Engineering",
    description:
      "Runs in a persistent cloud VM with full CLI, git, compilers, and API integrations to automate your backend.",
    url: "tank:@uriva/p2b-coder",
  },
  {
    id: "brand-guardian",
    name: "Brand & Content Guardian",
    division: "Design",
    description:
      "Maintains brand voice consistency, audits creative assets, and produces publication-ready content.",
    url:
      "https://github.com/msitarzewski/agency-agents/blob/main/design/design-brand-guardian.md",
  },
  {
    id: "analytics-reporter",
    name: "Analytics & KPI Reporter",
    division: "Support",
    description:
      "Transforms raw business data into actionable dashboards, tracks conversion KPIs, and alerts you to anomalies.",
    url:
      "https://github.com/msitarzewski/agency-agents/blob/main/support/support-analytics-reporter.md",
  },
];

const loadedTemplateImages = new Set<string>();

const ThemedTemplateImage = (
  { id, alt, className }: { id: string; alt: string; className?: string },
) => {
  const [loaded, setLoaded] = useState(() => loadedTemplateImages.has(id));
  const lightRef = useRef<HTMLImageElement>(null);
  const darkRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (
      loadedTemplateImages.has(id) ||
      lightRef.current?.complete ||
      darkRef.current?.complete
    ) {
      loadedTemplateImages.add(id);
      setLoaded(true);
    }
  }, [id]);

  const handleLoad = () => {
    loadedTemplateImages.add(id);
    setLoaded(true);
  };

  return (
    <div
      className={`relative overflow-hidden bg-surface-overlay-light dark:bg-surface-overlay ${
        className ?? ""
      }`}
    >
      {!loaded && (
        <div className="absolute inset-0 bg-surface-overlay-light dark:bg-surface-overlay animate-pulse" />
      )}
      <img
        ref={lightRef}
        src={`/templates/${id}-light.webp`}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        className="dark:hidden w-full h-full object-cover"
      />
      <img
        ref={darkRef}
        src={`/templates/${id}-dark.webp`}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        className="hidden dark:block w-full h-full object-cover"
      />
    </div>
  );
};

const HeroTemplateCarousel = () => {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const current = curatedHeroTemplates[index] ?? curatedHeroTemplates[0];
  const templateLaunchUrl = `${talkToSkillRoute}?url=${
    encodeURIComponent(current.url)
  }`;

  useEffect(() => {
    curatedHeroTemplates.forEach((template) => {
      const imgLight = new Image();
      imgLight.src = `/templates/${template.id}-light.webp`;
      imgLight.onload = () => loadedTemplateImages.add(template.id);
      const imgDark = new Image();
      imgDark.src = `/templates/${template.id}-dark.webp`;
      imgDark.onload = () => loadedTemplateImages.add(template.id);
    });
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % curatedHeroTemplates.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [isPaused]);

  return (
    <div
      className="w-full max-w-[680px] flex flex-col items-center gap-4 animate-fade-up stagger-3"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="w-full rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised overflow-hidden shadow-lg transition-colors hover:border-accent/40">
        <div className="flex flex-col sm:flex-row sm:h-[280px]">
          <div className="sm:w-5/12 relative aspect-[16/10] sm:aspect-auto sm:h-full border-b sm:border-b-0 sm:border-r border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light dark:bg-surface-overlay flex-shrink-0">
            <ThemedTemplateImage
              id={current.id}
              alt={current.name}
              className="w-full h-full"
            />
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-surface-raised-light/90 dark:bg-surface-raised/90 text-text-primary-light dark:text-text-primary border border-border-subtle-light dark:border-border-subtle backdrop-blur-xs shadow-xs">
              {current.division}
            </div>
          </div>

          <div className="sm:w-7/12 p-5 sm:p-6 flex flex-col justify-between text-left flex-1 sm:h-full">
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-mono font-medium text-text-tertiary-light dark:text-text-tertiary">
                  {String(index + 1).padStart(2, "0")} / {String(
                    curatedHeroTemplates.length,
                  ).padStart(2, "0")}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setIndex((i) =>
                        (i - 1 + curatedHeroTemplates.length) %
                        curatedHeroTemplates.length
                      );
                      setIsPaused(true);
                    }}
                    className="p-1.5 rounded-lg border border-border-subtle-light dark:border-border-subtle bg-surface-light dark:bg-surface hover:bg-surface-overlay-light dark:hover:bg-surface-overlay hover:border-accent/40 text-text-primary-light dark:text-text-primary transition-colors cursor-pointer shadow-xs"
                    aria-label="Previous template"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIndex((i) => (i + 1) % curatedHeroTemplates.length);
                      setIsPaused(true);
                    }}
                    className="p-1.5 rounded-lg border border-border-subtle-light dark:border-border-subtle bg-surface-light dark:bg-surface hover:bg-surface-overlay-light dark:hover:bg-surface-overlay hover:border-accent/40 text-text-primary-light dark:text-text-primary transition-colors cursor-pointer shadow-xs"
                    aria-label="Next template"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="h-[3.75rem] flex items-start">
                <h3 className="text-xl sm:text-2xl font-display font-bold text-text-primary-light dark:text-text-primary leading-tight line-clamp-2">
                  {current.name}
                </h3>
              </div>

              <div className="h-[4.25rem] mt-1 flex items-start overflow-hidden">
                <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed line-clamp-3">
                  {current.description}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-border-subtle-light dark:border-border-subtle mt-auto">
              <a
                href={templateLaunchUrl}
                className="group w-full sm:w-auto px-5 py-2.5 rounded-xl font-semibold text-sm bg-accent hover:bg-accent-hover text-white transition-all shadow-sm text-center cursor-pointer inline-flex items-center justify-center gap-1.5"
              >
                <span>Launch this template</span>
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href={templatesRoute}
                className="group text-xs sm:text-sm font-medium text-accent-light dark:text-accent hover:underline whitespace-nowrap inline-flex items-center gap-1"
              >
                <span>Browse templates</span>
                <ArrowRightIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-1.5 max-w-full">
        {curatedHeroTemplates.map((template, i) => (
          <button
            key={template.id}
            type="button"
            onClick={() => {
              setIndex(i);
              setIsPaused(true);
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
              i === index
                ? "bg-accent text-white shadow-xs"
                : "bg-surface-overlay-light dark:bg-surface-overlay text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary border border-border-subtle-light dark:border-border-subtle hover:border-accent/30"
            }`}
          >
            {template.name.split(" ")[0]}
          </button>
        ))}
      </div>
    </div>
  );
};

const HeroSection = () => {
  const route = useLocation().route;
  return (
    <section className={sectionClass}>
      <div className="max-w-2xl mx-auto flex flex-col items-center gap-6">
        <h1 className="text-4xl md:text-6xl font-display font-bold text-text-primary-light dark:text-text-primary leading-tight text-center animate-fade-up stagger-1">
          AI agents for the work{" "}
          <span className="text-gradient">you keep repeating.</span>
        </h1>
        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary text-center max-w-lg animate-fade-up stagger-2">
          Describe a task in plain language. Build an agent for customer
          support, research, operations, or personal work. Connect the tools it
          needs and use it on the web or in chat.
        </p>
        <div className="flex flex-col items-center gap-4 w-full max-w-sm animate-fade-up stagger-3">
          <LoginCta text="Build an AI agent" className="w-full" />
          <a
            href={pricingSocialMeta.path}
            className="text-sm font-semibold text-accent-light dark:text-accent underline underline-offset-4"
          >
            View pricing and free trial terms
          </a>
          <div className="relative flex items-center w-full">
            <div className="flex-grow border-t border-border-default-light dark:border-border-default" />
            <span className="flex-shrink-0 mx-3 text-text-tertiary-light dark:text-text-tertiary text-xs font-medium uppercase tracking-wider">
              or
            </span>
            <div className="flex-grow border-t border-border-default-light dark:border-border-default" />
          </div>
          <CtaButton
            onClick={() => {
              fireAnalyticsEventLegacy("signin_guest_cta_click");
              posthog.capture("Signin Guest CTA Click");
              route("/login?guest=1");
            }}
            className="w-full bg-[#7c5cbf] hover:bg-[#6b4aae] border-[#7c5cbf] text-white"
            text={
              <span className="flex items-center justify-center gap-2">
                <ZapIcon className="w-4 h-4" />
                Try as Guest
                <span className="hidden sm:inline-block ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-white/15 border border-white/20">
                  No email
                </span>
              </span>
            }
          />
          <p className="text-xs leading-relaxed text-center text-text-secondary-light dark:text-text-secondary">
            {freeTrialTerms}
          </p>
        </div>
      </div>
    </section>
  );
};

const QuickStartCard = (
  { title, description, url }: (typeof quickStartUseCases)[number],
) => (
  <a
    href={url}
    className="group p-6 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:border-accent/40 transition-all shadow-sm hover:-translate-y-1 text-left flex flex-col justify-between"
  >
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent-light dark:text-accent mb-3">
        Common workflow
      </p>
      <h3 className="text-xl font-display font-semibold mb-3 text-text-primary-light dark:text-text-primary group-hover:text-accent-light dark:group-hover:text-accent transition-colors">
        {title}
      </h3>
      <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-5">
        {description}
      </p>
    </div>
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-light dark:text-accent group-hover:underline">
      <span>Launch agent</span>
      <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
    </span>
  </a>
);

const QuickStartSection = () => (
  <section
    className={`${sectionClass} bg-gradient-to-b from-accent-light/5 via-surface-overlay-light/40 to-surface-light dark:from-accent/10 dark:via-surface-raised/30 dark:to-surface`}
  >
    <div className="w-full max-w-4xl mx-auto flex flex-col items-center gap-10">
      <div className="text-center">
        <h2 className={sectionHeadingClass}>Start with one useful task</h2>
        <p className={sectionSubheadingClass}>
          Answer questions from your docs, research a topic, process data, or
          organize your day. Choose a starting point, connect any required
          accounts, and test it with your own task.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-5 w-full">
        {quickStartUseCases.map(({ title, description, url }) => (
          <QuickStartCard
            key={title}
            title={title}
            description={description}
            url={url}
          />
        ))}
      </div>
      <div className="w-full flex flex-col items-center gap-4 pt-6 border-t border-border-subtle-light dark:border-border-subtle">
        <div className="text-center mb-1">
          <h3 className="text-2xl font-display font-bold text-text-primary-light dark:text-text-primary mb-1">
            Explore agent templates
          </h3>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary">
            Templates are starting instructions. Connect the tools and accounts
            your workflow needs.
          </p>
        </div>
        <HeroTemplateCarousel />
      </div>
    </div>
  </section>
);

const tabClass = (active: boolean) =>
  `px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
    active
      ? "bg-accent text-white shadow-sm"
      : "text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary"
  }`;

const UseCaseCard = (
  { useCase }: { useCase: (typeof botUseCases)[number] },
) => (
  <div className="p-5 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:border-accent/30 transition-colors shadow-sm">
    <h4 className="text-lg font-display font-semibold mb-3 text-text-primary-light dark:text-text-primary">
      {useCase.title}
    </h4>
    <ul className="space-y-1.5">
      {useCase.bullets.map((bullet, index) => (
        <li
          key={index}
          className="flex items-start gap-2 text-sm text-text-secondary-light dark:text-text-secondary"
        >
          <span className="text-accent-light dark:text-accent mt-0.5">·</span>
          {bullet}
        </li>
      ))}
    </ul>
  </div>
);

const coderExample = `import { createBotApi } from "@prompt2bot/client";
import { createConversation, createIdentity, sendMessage } from "@alice-and-bot/core";

const bot = await createBotApi({ apiToken: "p2b_...", name: "Support", prompt: "Be helpful." });
const me = await createIdentity("me");
const { conversationId } = await createConversation(
  [me.publicSignKey, bot.aliceAndBotPublicId], "chat", me,
);
await sendMessage({ credentials: me, conversation: conversationId, message: { type: "text", text: "Hi!" } });
`;

const audienceCards: Record<"non-coder" | "coder", {
  subtitle: string;
  description: string;
  code?: string;
}> = {
  "non-coder": {
    subtitle: "No coding required",
    description:
      "Talk to our AI in plain English and describe the workflow you want. It sets up the tools, configures the logic, and deploys your agent for you — all through conversation.",
  },
  "coder": {
    subtitle: "Full API & SDK access",
    description:
      "Use our TypeScript SDK and REST API to spin up agents programmatically, wire custom backend tools, and automate workflows across your stack.",
    code: coderExample,
  },
};

const AudienceCard = (
  { card }: { card: (typeof audienceCards)[keyof typeof audienceCards] },
) => (
  <div className="p-5 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:border-accent/30 transition-colors shadow-sm">
    <p className="text-sm font-medium text-accent-light dark:text-accent mb-3">
      {card.subtitle}
    </p>
    <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
      {card.description}
    </p>
    {card.code && (
      <div className="mt-4">
        <CodeBlock code={card.code} lang="typescript" />
      </div>
    )}
  </div>
);

const AudienceSection = () => {
  const [selected, setSelected] = useState<"non-coder" | "coder">("non-coder");
  return (
    <section
      className={`${sectionClass} bg-surface-overlay-light/30 dark:bg-surface-raised/20`}
    >
      <div className="w-full max-w-4xl mx-auto">
        <h2 className={sectionHeadingClass}>Built for Everyone</h2>
        <p className={sectionSubheadingClass}>
          Describe your workflow in chat, or build with the API
        </p>
        <div className="flex justify-center mb-10">
          <div className="inline-flex gap-1 p-1 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle">
            <button
              type="button"
              onClick={() => setSelected("non-coder")}
              className={tabClass(selected === "non-coder")}
            >
              Non-Coder
            </button>
            <button
              type="button"
              onClick={() => setSelected("coder")}
              className={tabClass(selected === "coder")}
            >
              Coder
            </button>
          </div>
        </div>
        <AudienceCard card={audienceCards[selected]} />
      </div>
    </section>
  );
};

const AgentKindPicker = () => {
  const [selected, setSelected] = useState<"front" | "team">("front");
  const useCases = selected === "front" ? frontUseCases : teamUseCases;
  return (
    <section
      className={`${sectionClass} bg-surface-overlay-light/30 dark:bg-surface-raised/20`}
    >
      <div className="w-full max-w-4xl mx-auto">
        <h2 className={sectionHeadingClass}>What do you want to automate?</h2>
        <p className={sectionSubheadingClass}>
          Explore ready-to-deploy agents for business workflows and daily
          operations
        </p>
        <div className="flex justify-center mb-10">
          <div className="inline-flex gap-1 p-1 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle">
            <button
              type="button"
              onClick={() => setSelected("front")}
              className={tabClass(selected === "front")}
            >
              For Business
            </button>
            <button
              type="button"
              onClick={() => setSelected("team")}
              className={tabClass(selected === "team")}
            >
              For Life & Operations
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5 min-h-[480px] md:min-h-[520px] content-start">
          {useCases.map((useCase) => (
            <UseCaseCard key={useCase.id} useCase={useCase} />
          ))}
        </div>
      </div>
    </section>
  );
};

const CapabilityCard = (
  { cap }: { cap: (typeof capabilities)[number] },
) => (
  <div className="p-5 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:border-accent/30 transition-colors shadow-sm">
    <div className="mb-3">
      <cap.icon />
    </div>
    <h3 className="text-lg font-display font-semibold mb-2 text-text-primary-light dark:text-text-primary">
      {cap.title}
    </h3>
    <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
      {cap.description}
    </p>
  </div>
);

const Capabilities = () => (
  <section className={sectionClass}>
    <div className="w-full max-w-4xl mx-auto">
      <h2 className={sectionHeadingClass}>Agent Infrastructure</h2>
      <p className={sectionSubheadingClass}>
        Every assistant comes with real execution power
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {capabilities.map((cap, i) => <CapabilityCard key={i} cap={cap} />)}
      </div>
    </div>
  </section>
);

const ChannelCard = (
  { icon: Icon, name, detail }: {
    icon: () => ComponentChildren;
    name: string;
    detail: string;
  },
) => (
  <div className="p-5 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:border-accent/30 transition-colors shadow-sm">
    <div className="mb-3">
      <Icon />
    </div>
    <h3 className="text-lg font-display font-semibold mb-2 text-text-primary-light dark:text-text-primary">
      {name}
    </h3>
    <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
      {detail}
    </p>
  </div>
);

const Channels = () => (
  <section
    className={`${sectionClass} bg-surface-overlay-light/30 dark:bg-surface-raised/20`}
  >
    <div className="w-full max-w-4xl mx-auto">
      <h2 className={sectionHeadingClass}>Choose where you use your agent</h2>
      <p className={sectionSubheadingClass}>
        Start on the web. Connect messaging channels when your workflow needs
        them. WhatsApp is optional.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {channels.map(({ icon, name, detail }) => (
          <ChannelCard key={name} icon={icon} name={name} detail={detail} />
        ))}
      </div>
    </div>
  </section>
);

const PricingSectionWrapper = () => (
  <section className={sectionClass}>
    <div className="w-full mx-auto" style={{ maxWidth: "1200px" }}>
      <PricingSection />
    </div>
  </section>
);

const BlogPostCard = (
  { slug, title, date, content }: {
    slug: string;
    title: string;
    date: string;
    content: string;
  },
) => (
  <a
    href={`/blog/${slug}`}
    className="block rounded-xl bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle hover:border-accent/30 transition-all group overflow-hidden shadow-sm"
  >
    <ThemedBlogImage
      slug={slug}
      alt={title}
      className="w-full h-40 object-cover"
    />
    <div className="p-5">
      <h4 className="text-lg font-display font-semibold text-text-primary-light dark:text-text-primary group-hover:text-accent-light dark:group-hover:text-accent transition-colors">
        {title}
      </h4>
      <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
        {date}
      </p>
      <p className="text-sm text-text-secondary-light dark:text-text-secondary mt-2 leading-relaxed">
        {extractExcerpt(content)}
      </p>
    </div>
  </a>
);

const BlogPosts = () => (
  <section
    className={`${sectionClass} bg-surface-overlay-light/30 dark:bg-surface-raised/20`}
  >
    <style>
      {`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}
    </style>
    <div className="w-full max-w-4xl mx-auto">
      <h2 className={sectionHeadingClass}>Learn</h2>
      <p className={sectionSubheadingClass}>
        Thoughts on agents, architecture, and shipping fast
      </p>
      <div className="grid md:grid-cols-2 gap-5">
        {posts.map(({ slug, title, date, content }) => (
          <BlogPostCard
            key={slug}
            slug={slug}
            title={title}
            date={date}
            content={content}
          />
        ))}
      </div>
    </div>
  </section>
);

const GitHubIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
  </svg>
);

const OpenSourceSection = () => (
  <div className="max-w-3xl mx-auto text-center py-12">
    <h3 className="text-xl font-display font-semibold mb-3 text-text-primary-light dark:text-text-primary">
      Built on great open source
    </h3>
    <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-6">
      prompt2bot relies on open source libraries and skill infrastructure.
    </p>
    <div className="flex flex-wrap justify-center gap-4">
      {openSourceProjects.map(({ name, url, description }) => (
        <a
          key={name}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 px-5 py-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:border-accent/40 transition-all group shadow-sm"
        >
          <GitHubIcon className="w-5 h-5 text-text-tertiary-light dark:text-text-tertiary group-hover:text-accent-light dark:group-hover:text-accent transition-colors" />
          <div className="text-left">
            <div className="text-sm font-semibold text-text-primary-light dark:text-text-primary group-hover:text-accent-light dark:group-hover:text-accent transition-colors">
              {name}
            </div>
            <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
              {description}
            </div>
          </div>
        </a>
      ))}
    </div>
  </div>
);

const FooterSection = () => {
  const route = useLocation().route;
  return (
    <section className={`${sectionClass} gap-8`}>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <CtaButton
          onClick={() => route(talkToSkillRoute)}
          text="Talk to a skill"
        />
        <CtaButton onClick={() => route("/docs")} text="API Docs" />
      </div>
      <OpenSourceSection />
      <LoginCta text="Build an AI agent" />
      <LegalFooter />
    </section>
  );
};

export const LandingPage = () => {
  usePageMeta(landingSocialMeta);
  const { error } = useAuth();
  if (error) {
    return (
      <div className="text-center py-10 text-danger">
        Error: {error.message}
      </div>
    );
  }
  return (
    <div className="bg-surface-light dark:bg-surface grain overflow-x-hidden">
      <HeroSection />
      <QuickStartSection />
      <PricingSectionWrapper />
      <AudienceSection />
      <AgentKindPicker />
      <Capabilities />
      <Channels />
      <ComparisonSection />
      <BlogPosts />
      <FooterSection />
    </div>
  );
};
